import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Bonuses from './bonuses/bonuses';
import cx from 'classnames';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { fetchMyLevel } from 'store/authSlice';
import { setRewardsTab } from 'store/uiSlice';
import { fetchBonusMatrix, fetchLevels } from 'store/levelSlice';
import { fetchGeoStatus } from 'store/siteSlice';
import LevelUp from './levelUp/levelUp';
import VipAllLevels from './vipAllLevels/vipAllLevels';
import { CloseIcon, RewardsIcon } from 'components/icons';
import LevelProgress from 'components/levelProgress/levelProgress';
import { useIsPlayingRealGame } from 'hooks/useIsPlayingRealGame';
import { bonusesApi } from 'store/bonusesApi';
import styles from './rewards.module.scss';

interface RewardsProps {
  isOpen: boolean;
  onClose: () => void;
}

const Rewards: React.FC<RewardsProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((state) => state.ui.rewardsActiveTab);
  const [isSpinning, setIsSpinning] = useState(false);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { bonusMatrix, isBonusMatrixLoading } = useAppSelector((state) => state.level);
  const config = useAppSelector((state) => state.site.config);
  const isBlocked = config?.geo?.isBlocked;
  const [configChecked, setConfigChecked] = useState(false);
  const isPlayingRealGame = useIsPlayingRealGame();

  const handleClose = () => {
    if (isSpinning) return;
    onClose();
  };

  const handleTabChange = (tab: string) => {
    if (isSpinning) return;
    dispatch(setRewardsTab(tab));
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchGeoStatus())
        .unwrap()
        .then(() => setConfigChecked(true))
        .catch(() => setConfigChecked(true));

      // If user opens rewards while playing a real game, manually refetch bonuses
      if (isPlayingRealGame) {
        dispatch(bonusesApi.util.invalidateTags(['Bonus']));
      }
    }
    return () => {
      setConfigChecked(false);
    };
  }, [isOpen, isPlayingRealGame, dispatch]);

  useEffect(() => {
    if (isOpen && configChecked && !isBlocked && !bonusMatrix && !isBonusMatrixLoading) {
      dispatch(fetchBonusMatrix());
    }
  }, [isOpen, configChecked, isBlocked, bonusMatrix, isBonusMatrixLoading, dispatch]);

  useEffect(() => {
    if (isOpen && configChecked && !isBlocked && activeTab === 'bonuses' && isAuthenticated) {
      dispatch(fetchMyLevel());
    }
  }, [isOpen, configChecked, isBlocked, activeTab, isAuthenticated, dispatch]);

  useEffect(() => {
    if (isOpen && configChecked && !isBlocked && isAuthenticated) {
      dispatch(fetchLevels());
    }
  }, [isOpen, configChecked, isBlocked, isAuthenticated, dispatch]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      const otherModals = document.querySelectorAll(
        '[class*="modalOverlay"], [class*="searchBackdrop"]',
      );
      if (otherModals.length <= 1) {
        document.body.style.overflow = '';
      }
    }
    return () => {
      const otherModals = document.querySelectorAll(
        '[class*="modalOverlay"], [class*="searchBackdrop"]',
      );
      if (otherModals.length <= 1) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  // Lock mobile/browser back button during active wheel spin
  useEffect(() => {
    if (!isOpen || !isSpinning) return;

    window.history.pushState({ isSpinningLock: true }, '');

    const handlePopState = () => {
      window.history.pushState({ isSpinningLock: true }, '');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.isSpinningLock) {
        window.history.back();
      }
    };
  }, [isOpen, isSpinning]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleArea}>
            <RewardsIcon className={styles.titleIcon} />
            <h2>{t('rewards.title')}</h2>
          </div>
          <button
            className={cx(styles.closeBtn, { [styles.disabled]: isSpinning })}
            onClick={handleClose}
            aria-label={t('rewards.close')}
            disabled={isSpinning}
          >
            <CloseIcon />
          </button>
        </div>
        {isAuthenticated && user?.level && (
          <LevelProgress
            levelName={user.level?.next?.name ?? t('rewards.levelProgress.finish')}
            progressPercent={user.level.progressPercent}
            pointsUntilNext={user.level?.pointsUntilNext ?? ''}
          />
        )}

        <div
          className={styles.tabs}
          data-active={activeTab}
          role="tablist"
          aria-label={t('rewards.title')}
        >
          <button
            type="button"
            className={cx(styles.tab, {
              [styles.active]: activeTab === 'bonuses',
              [styles.disabled]: isSpinning,
            })}
            onClick={() => handleTabChange('bonuses')}
            disabled={isSpinning}
            title={t('rewards.tabs.bonuses')}
            role="tab"
            aria-selected={activeTab === 'bonuses'}
          >
            <span className={styles.tabLabel}>{t('rewards.tabs.bonuses')}</span>
          </button>
          <button
            type="button"
            className={cx(styles.tab, {
              [styles.active]: activeTab === 'levelUp',
              [styles.disabled]: isSpinning,
            })}
            onClick={() => handleTabChange('levelUp')}
            disabled={isSpinning}
            title={t('rewards.tabs.levelUp')}
            role="tab"
            aria-selected={activeTab === 'levelUp'}
          >
            <span className={styles.tabLabel}>{t('rewards.tabs.levelUp')}</span>
          </button>
          <button
            type="button"
            className={cx(styles.tab, {
              [styles.active]: activeTab === 'allLevels',
              [styles.disabled]: isSpinning,
            })}
            onClick={() => handleTabChange('allLevels')}
            disabled={isSpinning}
            title={t('rewards.tabs.allLevels')}
            role="tab"
            aria-selected={activeTab === 'allLevels'}
          >
            <span className={styles.tabLabel}>{t('rewards.tabs.allLevels')}</span>
          </button>
        </div>

        <div className={styles.scrollableContent}>
          {activeTab === 'bonuses' && <Bonuses />}
          {activeTab === 'levelUp' && <LevelUp onSpinningChange={setIsSpinning} />}
          {activeTab === 'allLevels' && <VipAllLevels />}
        </div>
      </div>
    </div>
  );
};

export default Rewards;
